/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { EXRLoader  } from 'three/addons/loaders/EXRLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { Midi } from '@tonejs/midi';

import { mm } from './units';
import {
  KEY_W, KEY_H, KEY_D,
  CUT_W, CUT_D,
  FLASH_SEC, FLASH_COLOR_WHITE, FLASH_COLOR_BLACK,
  SEMI_NOTCHES,
  isWhite, hasBlackAfter,
} from './constants';
import { fibonacciPoints, corkscrewPoints } from './layout';
import { createParticleSystem, KeyInfo } from './particles';

let __kbdCounter = 0;

interface Props {
  canvasRef:       React.RefObject<HTMLCanvasElement>;
  startNote:       number;
  numKeys:         number;
  gap?:            number;
  blackHeight?:    number;
  endNote?:        number;
  groupId?:        string;
  layoutMode?:     'keyboard' | 'sphericalFibonacci' | 'corkscrew';
  corkscrewTurns?: number;
  corkscrewBias?:  number;
  midiFile?:       File;
  particleMode?:   boolean;
  particleCount?:  number;
  /** Path (URL or import) to an env-map texture. */
  envMapPath?:     string;
  /** Explicit type if the extension is ambiguous. */
  envMapType?:     'hdr' | 'exr' | 'ktx2';
}

export default function ThreeScene({
  canvasRef,
  startNote,
  numKeys,
  gap,
  blackHeight     = mm(12.5),
  endNote,
  groupId,
  layoutMode      = 'keyboard',
  corkscrewTurns  = 4,
  corkscrewBias   = 0,
  midiFile,
  particleMode    = false,
  particleCount   = 0,
  envMapPath,
  envMapType = 'hdr',
}: Props) {
  const sceneRef      = useRef<THREE.Scene>(null!);
  const cameraRef     = useRef<THREE.PerspectiveCamera>(null!);
  const controlsRef   = useRef<OrbitControls>(null!);
  const rendererRef   = useRef<THREE.WebGLRenderer>(null!);

  const whiteMeshRef  = useRef<THREE.InstancedMesh>(null!);
  const blackMeshRef  = useRef<THREE.InstancedMesh>(null!);

  const whiteMatRef   = useRef<THREE.MeshPhysicalMaterial>(null!);
  const blackMatRef   = useRef<THREE.MeshPhysicalMaterial>(null!);

  const particleSysRef = useRef<ReturnType<typeof createParticleSystem> | null>(null);

  const seqRef        = useRef<{ note: number; type: 'white' | 'black' }[]>([]);
  const whiteListRef  = useRef<{ note: number; type: 'white' }[]>([]);
  const blackListRef  = useRef<{ note: number; type: 'black' }[]>([]);

  const keyCentersRef = useRef<KeyInfo[]>([]);

  const flashRef      = useRef<Map<string, number>>(new Map());

  const eventsRef     = useRef<{ time: number; midi: number }[]>([]);
  const loopDurRef    = useRef<number>(0);
  const loopStart     = useRef<number>(0);
  const prevT         = useRef<number>(0);

  const audioCtxRef   = useRef<AudioContext | null>(null);

  const windowFocused = useRef(true);
  const pauseStarted  = useRef<number | null>(null);

  const dragging      = useRef(false);
  const pointer       = useRef(new THREE.Vector2()).current;
  const raycaster     = useRef(new THREE.Raycaster()).current;
  const lastMidiDrag  = useRef<number | null>(null);
  const lastTrigRef   = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!midiFile) { eventsRef.current = []; loopDurRef.current = 0; return; }
    const fr = new FileReader();
    fr.onload = () => {
      try {
        const midi = new Midi(fr.result as ArrayBuffer);
        const evts: { time: number; midi: number }[] = [];
        midi.tracks.forEach(t => t.notes.forEach(n => evts.push({ time: n.time, midi: n.midi })));
        evts.sort((a, b) => a.time - b.time);
        eventsRef.current  = evts;
        loopDurRef.current = midi.duration || evts[evts.length - 1]?.time || 0;
        loopStart.current  = performance.now() / 1000;
        prevT.current      = 0;
      } catch (e) { console.error('MIDI parse failed', e); }
    };
    fr.readAsArrayBuffer(midiFile);
  }, [midiFile]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.setPixelRatio(devicePixelRatio);
    renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('black');
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(60, canvas.clientWidth / canvas.clientHeight, 0.1, 100);
    camera.position.set(0, 0, 10);
    cameraRef.current = camera;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controlsRef.current = controls;


    const whiteGeo = new THREE.BoxGeometry(KEY_W, KEY_H, KEY_D, 3, 1, 2).toNonIndexed();
    const blackGeo = new THREE.BoxGeometry(
      (gap ?? mm(1)) + 2 * CUT_W,
      KEY_H + blackHeight,
      CUT_D,
    ).toNonIndexed();

    const whiteMat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      metalness: 0.0,
      roughness: 0.15,
      reflectivity: 0.5,
      clearcoat: 0.4,
      clearcoatRoughness: 0.1,
      envMapIntensity: 1.0,
    });
    whiteMat.onBeforeCompile = (s) => {
      Object.assign(s.uniforms, {
        uHalfW: { value: KEY_W * 0.5 }, uHalfD: { value: KEY_D * 0.5 },
        uHalfH: { value: KEY_H * 0.5 }, uCutW:  { value: CUT_W },
        uCutD:  { value: CUT_D },
      });

      s.vertexShader = s.vertexShader
        .replace('#include <common>', `
          #include <common>
          attribute float aEnableLeft;
          attribute float aEnableRight;
          varying   float vEnableLeft;
          varying   float vEnableRight;
          varying   vec3  vPos;
          uniform float uHalfW, uHalfD, uCutW, uCutD;
          #define EPS 1e-4
        `)
        .replace('#include <begin_vertex>', `
          vec3 transformed = position;
          bool onBack = transformed.z >  uHalfD - EPS;
          bool rightC = onBack && transformed.x < -uHalfW + EPS;
          bool leftC  = onBack && transformed.x >  uHalfW  - EPS;

          if (rightC && (aEnableRight > 0.5)) {
            transformed.x += uCutW;
            transformed.z -= uCutD;
          }
          if (leftC && (aEnableLeft > 0.5)) {
            transformed.x -= uCutW;
            transformed.z -= uCutD;
          }
          vPos = transformed;
          vEnableLeft  = aEnableLeft;
          vEnableRight = aEnableRight;
        `);

      s.fragmentShader = s.fragmentShader
        .replace('void main()', `
          varying vec3  vPos;
          varying float vEnableLeft;
          varying float vEnableRight;
          uniform float uHalfW, uHalfD, uHalfH, uCutW, uCutD;
          #define EPS 1e-4
          void main()
        `)
        .replace('#include <premultiplied_alpha_fragment>', `
          bool eL = vEnableLeft  > 0.5;
          bool eR = vEnableRight > 0.5;
          bool top = vPos.y >  uHalfH - EPS;
          bool bot = vPos.y < -uHalfH + EPS;

          bool inLT = eL && top &&
                      vPos.z > uHalfD - uCutD + EPS &&
                      vPos.x >  uHalfW - uCutW + EPS;

          bool inRT = eR && top &&
                      vPos.z > uHalfD - uCutD + EPS &&
                      vPos.x < -uHalfW + uCutW - EPS;

          bool inLB = eL && bot &&
                      vPos.z > uHalfD - uCutD + EPS &&
                      vPos.x >  uHalfW - uCutW + EPS;

          bool inRB = eR && bot &&
                      vPos.z > uHalfD - uCutD + EPS &&
                      vPos.x < -uHalfW + uCutW - EPS;

          if (inLT || inRT || inLB || inRB) discard;
          #include <premultiplied_alpha_fragment>
        `);
    };
    whiteMatRef.current = whiteMat;

    const blackMat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      metalness: 0.0,
      roughness: 0.15,
      reflectivity: 0.5,
      clearcoat: 0.4,
      clearcoatRoughness: 0.1,
      envMapIntensity: 1.0,
    });
    blackMatRef.current = blackMat;

    const seq: { note: number; type: 'white' | 'black' }[] = [];
    if (endNote !== undefined && endNote > startNote) {
      const pattern: typeof seq = [];
      for (let n = startNote; n <= endNote; n++) {
        if (isWhite(n)) {
          pattern.push({ note: n, type: 'white' });
          if (hasBlackAfter(n % 12) && n + 1 <= endNote) pattern.push({ note: n + 1, type: 'black' });
        }
      }
      for (let i = 0; seq.length < numKeys; i = (i + 1) % pattern.length) seq.push({ ...pattern[i] });
    } else {
      for (let n = startNote; seq.length < numKeys;) {
        if (isWhite(n)) {
          seq.push({ note: n, type: 'white' });
          if (hasBlackAfter(n % 12) && seq.length < numKeys) seq.push({ note: n + 1, type: 'black' });
        }
        n++;
      }
    }
    seqRef.current = seq;

    const whiteList = seq.filter(s => s.type === 'white') as { note: number; type: 'white' }[];
    const blackList = seq.filter(s => s.type === 'black') as { note: number; type: 'black' }[];
    whiteListRef.current = whiteList;
    blackListRef.current = blackList;

    const keyboard = new THREE.Group();
    keyboard.name = groupId ?? `kbd-${__kbdCounter++}`;
    if (layoutMode === 'keyboard') keyboard.rotation.y = Math.PI;
    scene.add(keyboard);

    const whiteMesh = new THREE.InstancedMesh(whiteGeo, whiteMat, whiteList.length);
    const blackMesh = new THREE.InstancedMesh(blackGeo, blackMat, blackList.length);
    whiteMeshRef.current = whiteMesh;
    blackMeshRef.current = blackMesh;
    keyboard.add(whiteMesh);
    keyboard.add(blackMesh);

    const whiteBase = new THREE.Color(0xffffff);
    const blackBase = new THREE.Color(0x111111);
    for (let i = 0; i < whiteList.length; i++) whiteMesh.setColorAt(i, whiteBase);
    for (let i = 0; i < blackList.length; i++) blackMesh.setColorAt(i, blackBase);
    whiteMesh.instanceColor!.needsUpdate = true;
    blackMesh.instanceColor!.needsUpdate = true;

    const leftFlags  = new Float32Array(whiteList.length);
    const rightFlags = new Float32Array(whiteList.length);
    whiteList.forEach((s, i) => {
      const [l, r] = SEMI_NOTCHES[s.note % 12];
      leftFlags[i]  = l;
      rightFlags[i] = r;
    });
    leftFlags[0] = 0;
    rightFlags[rightFlags.length - 1] = 0;
    whiteGeo.setAttribute('aEnableLeft',  new THREE.InstancedBufferAttribute(leftFlags, 1));
    whiteGeo.setAttribute('aEnableRight', new THREE.InstancedBufferAttribute(rightFlags, 1));

    const applyLayout = (bias: number, turns: number) => {
      const tmpM    = new THREE.Matrix4();
      const forward = new THREE.Vector3();
      const up      = new THREE.Vector3();
      const right   = new THREE.Vector3();

      let wi = 0, bi = 0;
      keyCentersRef.current.length = 0;

      const points =
        layoutMode === 'sphericalFibonacci' ? fibonacciPoints(seq.length, 3) :
        layoutMode === 'corkscrew'          ? corkscrewPoints(seq.length, 3, turns, bias) :
        [];

      const spacing = KEY_W + (gap ?? mm(1));
      const mid     = (whiteList.length - 1) / 2;

      seq.forEach((s, i) => {
        const { type, note } = s;
        const idx = type === 'white' ? wi : bi;

        if (layoutMode === 'keyboard') {
          tmpM.makeTranslation(
            (mid - wi) * spacing,
            type === 'black' ? blackHeight * 0.5 : 0,
            type === 'white' ? 0 : KEY_D * 0.5 - CUT_D * 0.5,
          );
        } else {
          const pos = points[i];
          forward.copy(pos).normalize().negate();
          up.set(0, 1, 0);
          if (Math.abs(forward.dot(up)) > 0.99) up.set(1, 0, 0);
          right.crossVectors(up, forward).normalize();
          up.crossVectors(forward, right).normalize();

          const outward = forward.clone().negate();
          const depth   = type === 'white' ? KEY_D : CUT_D;
          const shifted = pos.clone().add(outward.multiplyScalar(depth * 0.5));

          tmpM.makeBasis(right, up, forward);
          tmpM.setPosition(shifted);
        }

        (type === 'white' ? whiteMesh : blackMesh).setMatrixAt(idx, tmpM);

        const centre = new THREE.Vector3().setFromMatrixPosition(tmpM);
        keyCentersRef.current.push({ pos: centre, midi: note, isWhite: type === 'white', idx });

        type === 'white' ? wi++ : bi++;
      });

      whiteMesh.instanceMatrix.needsUpdate = true;
      blackMesh.instanceMatrix.needsUpdate = true;
    };

    applyLayout(corkscrewBias, corkscrewTurns);

    const flashKey = (midi: number, idx: number, w: boolean) => {
      const flashColor = w ? FLASH_COLOR_WHITE : FLASH_COLOR_BLACK;
      (w ? whiteMesh : blackMesh).setColorAt(idx, flashColor);
      (w ? whiteMesh : blackMesh).instanceColor!.needsUpdate = true;
      flashRef.current.set(`${midi}_${idx}`, performance.now() / 1000 + FLASH_SEC);
    };
    const unflashKey = (_midi: number, idx: number, w: boolean) => {
      (w ? whiteMesh : blackMesh).setColorAt(idx, w ? whiteBase : blackBase);
      (w ? whiteMesh : blackMesh).instanceColor!.needsUpdate = true;
    };
    const flashAllKeys = (midi: number) => {
      whiteList.forEach((k, i) => { if (k.note === midi) flashKey(midi, i, true); });
      blackList.forEach((k, i) => { if (k.note === midi) flashKey(midi, i, false); });
    };

    const playMidi = (midi: number, dur = 0.4) => {
      const ctx = audioCtxRef.current!;
      if (ctx.state === 'suspended') ctx.resume();
      const now = ctx.currentTime;
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 440 * Math.pow(2, (midi - 69) / 12);
      gain.gain.setValueAtTime(0.18, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(now + dur);
    };

    const updatePointer = (ev: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
    };
    const pointerDown = (ev: PointerEvent) => {
      updatePointer(ev);
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects([whiteMesh, blackMesh], false);
      if (!hits.length) return;
      const hit  = hits[0] as THREE.Intersection<THREE.InstancedMesh>;
      const idx  = hit.instanceId!;
      const isW  = hit.object === whiteMesh;
      const midi = isW ? whiteList[idx].note : blackList[idx].note;

      playMidi(midi);
      flashKey(midi, idx, isW);
      lastMidiDrag.current = midi;

      controls.enabled = false;
      dragging.current = true;
    };
    const pointerMove = (ev: PointerEvent) => updatePointer(ev);
    const pointerUp   = () => {
      if (dragging.current) {
        controls.enabled = true;
        dragging.current = false;
        lastMidiDrag.current = null;
      }
    };
    renderer.domElement.addEventListener('pointerdown', pointerDown);
    renderer.domElement.addEventListener('pointermove', pointerMove);
    window.addEventListener('pointerup',   pointerUp);

    const onBlur = () => {
      if (!windowFocused.current) return;
      windowFocused.current = false;
      pauseStarted.current  = performance.now() / 1000;
      audioCtxRef.current?.suspend();
    };
    const onFocus = () => {
      if (windowFocused.current) return;
      windowFocused.current = true;
      const now = performance.now() / 1000;
      if (pauseStarted.current !== null && loopDurRef.current > 0) {
        const lost = now - pauseStarted.current;
        loopStart.current += lost;
        prevT.current = (now - loopStart.current) % loopDurRef.current;
      }
      pauseStarted.current = null;
      audioCtxRef.current?.resume();
    };
    window.addEventListener('blur',  onBlur);
    window.addEventListener('focus', onFocus);

    const onResize = () => {
      renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
      camera.aspect = canvas.clientWidth / canvas.clientHeight;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', onResize);

    renderer.setAnimationLoop(() => {
      const now = performance.now() / 1000;

      if (dragging.current) {
        raycaster.setFromCamera(pointer, camera);
        const hits = raycaster.intersectObjects([whiteMesh, blackMesh], false);
        if (hits.length) {
          const hit  = hits[0] as THREE.Intersection<THREE.InstancedMesh>;
          const idx  = hit.instanceId!;
          const isW  = hit.object === whiteMesh;
          const midi = isW ? whiteList[idx].note : blackList[idx].note;
          if (midi !== lastMidiDrag.current) {
            playMidi(midi);
            flashKey(midi, idx, isW);
            lastMidiDrag.current = midi;
          }
        } else {
          lastMidiDrag.current = null;
        }
      }

      if (windowFocused.current && eventsRef.current.length && loopDurRef.current > 0) {
        const tLoop   = (now - loopStart.current) % loopDurRef.current;
        const prev    = prevT.current;
        const wrapped = tLoop < prev;

        const trigger = (a: number, b: number) => {
          eventsRef.current.forEach(e => {
            if (e.time >= a && e.time < b) {
              playMidi(e.midi);
              flashAllKeys(e.midi);
            }
          });
        };
        wrapped ? (trigger(prev, loopDurRef.current), trigger(0, tLoop))
                :  trigger(prev, tLoop);

        prevT.current = tLoop;
      }

      if (particleSysRef.current) {
        particleSysRef.current.update(now, keyCentersRef.current, (k) => {
          const keyId = `p_${k.midi}_${k.idx}`;
          const last  = lastTrigRef.current.get(keyId) ?? 0;
          if (now - last > 0.25) {
            playMidi(k.midi);
            flashKey(k.midi, k.idx, k.isWhite);
            lastTrigRef.current.set(keyId, now);
          }
        });
      }

      flashRef.current.forEach((end, key) => {
        if (now >= end) {
          const [midiStr, idxStr] = key.split('_');
          const midi = Number(midiStr);
          const idx  = Number(idxStr);
          const isW  = isWhite(midi);
          unflashKey(midi, idx, isW);
          flashRef.current.delete(key);
        }
      });

      controls.update();
      renderer.render(scene, camera);
    });

    return () => {
      renderer.setAnimationLoop(null);
      renderer.dispose();
      controls.dispose();

      renderer.domElement.removeEventListener('pointerdown', pointerDown);
      renderer.domElement.removeEventListener('pointermove', pointerMove);
      window.removeEventListener('pointerup',   pointerUp);
      window.removeEventListener('resize',      onResize);
      window.removeEventListener('blur',        onBlur);
      window.removeEventListener('focus',       onFocus);
    };
  }, [
    canvasRef,
    startNote,
    numKeys,
    endNote,
    gap,
    blackHeight,
    layoutMode,
  ]);

  useEffect(() => {
    if (!envMapPath) return;
    const scene    = sceneRef.current;
    const renderer = rendererRef.current;
    if (!scene || !renderer) return;

    let disposed   = false;
    let currentEnv: THREE.Texture | null = null;

    scene.background  = new THREE.Color(0x202020);
    scene.environment = null;

    const pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();

    const lower  = envMapPath.toLowerCase();
    const isKTX2 = lower.endsWith('.ktx2') || envMapType === 'ktx2';
    const isEXR  = lower.endsWith('.exr')  || envMapType === 'exr';

    const loader = isKTX2
      ? new KTX2Loader()
          .setTranscoderPath('/basis/')        // put basis/*.wasm & *.js here
          .detectSupport(renderer)
      : isEXR
          ? new EXRLoader().setDataType(THREE.HalfFloatType)
          : new RGBELoader().setDataType(THREE.HalfFloatType);

    const applyEnvToMaterials = () => {
      const env = scene.environment;
      if (!env) return;
      [whiteMatRef.current, blackMatRef.current].forEach((m) => {
        if (m && m.envMap !== env) {
          m.envMap = env;
          m.needsUpdate = true;
        }
      });
    };

    loader.load(
      envMapPath,
      (tex: THREE.Texture) => {
        if (disposed) { tex.dispose(); return; }

        if (isKTX2) {
          tex.mapping       = THREE.EquirectangularReflectionMapping;
          scene.environment = tex;
          scene.background  = tex;
          currentEnv        = tex;
          applyEnvToMaterials();
          return;
        }

        tex.mapping = THREE.EquirectangularReflectionMapping;

        const generate = () => {
          if (disposed) { tex.dispose(); return; }
          currentEnv = pmrem.fromEquirectangular(tex).texture;
          scene.environment = currentEnv;
          scene.background  = currentEnv;
          tex.dispose();
          applyEnvToMaterials();
        };

        (window as any).requestIdleCallback
          ? (window as any).requestIdleCallback(generate)
          : setTimeout(generate, 1);
      },
      undefined,
      (err: unknown) => console.error('Env-map load failed:', err),
    );

    return () => {
      disposed = true;
      currentEnv?.dispose();
      pmrem.dispose();
    };
  }, [envMapPath, envMapType]);

  useEffect(() => {
    if (layoutMode === 'keyboard') return;
    const whiteMesh = whiteMeshRef.current;
    const blackMesh = blackMeshRef.current;
    const seq       = seqRef.current;
    if (!whiteMesh || !blackMesh || !seq.length) return;

    const tmpM    = new THREE.Matrix4();
    const forward = new THREE.Vector3();
    const up      = new THREE.Vector3();
    const right   = new THREE.Vector3();

    keyCentersRef.current.length = 0;

    const whiteList = whiteListRef.current;

    let wi = 0, bi = 0;

    const points =
      layoutMode === 'sphericalFibonacci' ? fibonacciPoints(seq.length, 3) :
      corkscrewPoints(seq.length, 3, corkscrewTurns, corkscrewBias);

    const spacing = KEY_W + (gap ?? mm(1));
    const mid     = (whiteList.length - 1) / 2;

    seq.forEach((s, i) => {
      const { type, note } = s;
      const idx = type === 'white' ? wi : bi;

      if ((layoutMode as string) === 'keyboard') {
        tmpM.makeTranslation(
          (mid - wi) * spacing,
          type === 'black' ? blackHeight * 0.5 : 0,
          type === 'white' ? 0 : KEY_D * 0.5 - CUT_D * 0.5,
        );
      } else {
        const pos = points[i];
        forward.copy(pos).normalize().negate();
        up.set(0, 1, 0);
        if (Math.abs(forward.dot(up)) > 0.99) up.set(1, 0, 0);
        right.crossVectors(up, forward).normalize();
        up.crossVectors(forward, right).normalize();

        const shifted = pos.clone().add(
          forward.clone().negate().multiplyScalar((type === 'white' ? KEY_D : CUT_D) * 0.5),
        );
        tmpM.makeBasis(right, up, forward);
        tmpM.setPosition(shifted);
      }

      (type === 'white' ? whiteMesh : blackMesh).setMatrixAt(idx, tmpM);
      keyCentersRef.current.push({
        pos:  new THREE.Vector3().setFromMatrixPosition(tmpM),
        midi: note,
        isWhite: type === 'white',
        idx,
      });

      type === 'white' ? wi++ : bi++;
    });

    whiteMesh.instanceMatrix.needsUpdate = true;
    blackMesh.instanceMatrix.needsUpdate = true;
  }, [
    corkscrewBias,
    corkscrewTurns,
    layoutMode,
    gap,
    blackHeight,
  ]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (particleSysRef.current) {
      particleSysRef.current.dispose();
      particleSysRef.current = null;
    }

    if (particleMode && particleCount > 0) {
      particleSysRef.current = createParticleSystem(scene, particleCount);
    }

    return () => {
      particleSysRef.current?.dispose();
      particleSysRef.current = null;
    };
  }, [particleMode, particleCount]);

  return null;
}
